import {
  montarMultipartSolicitacao,
  normalizeAndamento,
  normalizeMensagem,
  normalizeProcesso,
} from '@/features/arbitral-detalhes';
import type {
  AndamentoProcesso,
  DocumentoProcesso,
  MensagemChatItem,
  PendenciaProcesso,
  ProcessoArbitralDetail,
  CriarSolicitacaoPayload,
  SolicitacaoAlteracao,
} from '@/features/arbitral-detalhes';
import { portalGet, portalPatch, portalPost } from '@/features/portal/utils/portalRequest';
import { buscarBytesRemotos, salvarBlobComoArquivo } from '@/shared/utils/browserActions';
import { assertUrlNavegavelSegura } from '@/shared/utils/urlSegura';

import type {
  AcaoRespostaPendencia,
  AndamentoDoAdvogadoInput,
  AutosDoAdvogado,
  IdCriado,
  PaginaChatDoAdvogado,
} from '../types';
import { unwrapData } from './services';

const autosBase = (processoId: string) => `/arbitral/portal/advogados/processos/${processoId}`;

/**
 * Dois saltos de propósito: o core assina (chamada com cookie do portal) e o browser busca os
 * bytes DIRETO do bucket. O `/preview` fazia o core bufferizar o arquivo inteiro na resposta, o
 * que estoura o teto de 32 MiB de resposta não-streamada do Cloud Run e derrubava com 500 todo
 * documento grande (decisão D12; mesmo par das outras duas superfícies).
 *
 * A URL é credencial de vida curta (`GCS_SIGNED_URL_TTL`, 300s, e a rota responde `no-store`):
 * peça imediatamente antes de usar, nunca guarde em estado de lista.
 */
async function urlAssinadaDoDocumento(processoId: string, documentoId: string): Promise<string> {
  const response = await portalGet(`${autosBase(processoId)}/documentos/${documentoId}/download`);
  const { url } = await unwrapData<{ url?: unknown }>(
    response,
    'Não foi possível gerar o link do documento.',
  );
  return assertUrlNavegavelSegura(url, 'Link do documento');
}

async function bytesDoDocumento(processoId: string, documentoId: string): Promise<Blob> {
  return buscarBytesRemotos(await urlAssinadaDoDocumento(processoId, documentoId));
}

export const advogadoAutosService = {
  obterAutos: async (processoId: string): Promise<AutosDoAdvogado> => {
    const response = await portalGet(`${autosBase(processoId)}/autos`);
    const bruto = await unwrapData<{
      processo: ProcessoArbitralDetail;
      escopoDeSolicitacao: AutosDoAdvogado['escopoDeSolicitacao'];
    }>(response, 'Não foi possível carregar o processo.');
    return {
      processo: normalizeProcesso(bruto.processo),
      escopoDeSolicitacao: bruto.escopoDeSolicitacao,
    };
  },

  listarAndamentos: async (processoId: string): Promise<AndamentoProcesso[]> => {
    const response = await portalGet(`${autosBase(processoId)}/andamentos`);
    const itens = await unwrapData<Record<string, unknown>[]>(
      response,
      'Não foi possível carregar os andamentos.',
    );
    return itens.map(normalizeAndamento);
  },

  criarAndamento: async (
    processoId: string,
    dados: AndamentoDoAdvogadoInput,
  ): Promise<AndamentoProcesso> => {
    const form = new FormData();
    form.append('descricao', dados.descricao);
    dados.arquivos.forEach((arquivo) => form.append('anexos', arquivo));
    const response = await portalPost(`${autosBase(processoId)}/andamentos`, form);
    const criado = await unwrapData<Record<string, unknown>>(
      response,
      'Não foi possível registrar o andamento.',
    );
    return normalizeAndamento(criado);
  },

  listarDocumentos: async (processoId: string): Promise<DocumentoProcesso[]> => {
    const response = await portalGet(`${autosBase(processoId)}/documentos`);
    return unwrapData<DocumentoProcesso[]>(response, 'Não foi possível carregar os documentos.');
  },

  // Salva o blob em vez de navegar até a signed URL: o atributo `download` do <a> é ignorado em
  // URL cross-origin, então navegar até o bucket perderia o nome original do arquivo.
  baixarDocumento: async (
    processoId: string,
    documentoId: string,
    nomeOriginal: string,
  ): Promise<void> => {
    salvarBlobComoArquivo(await bytesDoDocumento(processoId, documentoId), nomeOriginal);
  },

  // Object URL same-origin, e não a signed URL direto no visor: ela viaja com
  // `response-content-disposition: attachment` DENTRO da assinatura (`buildContentDisposition`
  // no core não tem ramo `inline`), então apontar um `<iframe>`/`<img>` para ela baixaria o
  // arquivo em vez de renderizá-lo. Quem revoga o object URL é o visor, como antes.
  previewDocumento: async (processoId: string, documentoId: string): Promise<string> =>
    window.URL.createObjectURL(await bytesDoDocumento(processoId, documentoId)),

  listarPendencias: async (processoId: string): Promise<PendenciaProcesso[]> => {
    const response = await portalGet(`${autosBase(processoId)}/pendencias`);
    return unwrapData<PendenciaProcesso[]>(response, 'Não foi possível carregar as pendências.');
  },

  responderPendencia: async (
    processoId: string,
    pendenciaId: string,
    acao: AcaoRespostaPendencia,
    resposta: string,
    arquivos: File[] = [],
  ): Promise<void> => {
    const form = new FormData();
    form.append('resposta', resposta);
    arquivos.forEach((arquivo) => form.append('arquivos', arquivo));
    const response = await portalPost(
      `${autosBase(processoId)}/pendencias/${pendenciaId}/${acao}`,
      form,
    );
    await unwrapData<IdCriado>(response, 'Não foi possível enviar a resposta.');
  },

  listarMensagensChat: async (
    processoId: string,
    page = 1,
    limit = 50,
  ): Promise<PaginaChatDoAdvogado> => {
    const response = await portalGet(`${autosBase(processoId)}/chat`, { page, limit });
    const data = await unwrapData<Record<string, unknown>>(
      response,
      'Não foi possível carregar as mensagens.',
    );
    return {
      mensagens: (data.mensagens as Record<string, unknown>[]).map(normalizeMensagem),
      total: Number(data.total ?? 0),
    };
  },

  enviarMensagemChat: async (
    processoId: string,
    texto: string,
    arquivos: File[],
  ): Promise<MensagemChatItem> => {
    const form = new FormData();
    if (texto.trim()) form.append('texto', texto.trim());
    arquivos.forEach((arquivo) => form.append('arquivos', arquivo));
    const response = await portalPost(`${autosBase(processoId)}/chat`, form);
    const enviada = await unwrapData<Record<string, unknown>>(
      response,
      'Não foi possível enviar a mensagem.',
    );
    return normalizeMensagem(enviada);
  },

  marcarMensagemLida: async (processoId: string, mensagemId: string): Promise<void> => {
    const response = await portalPatch(`${autosBase(processoId)}/chat/${mensagemId}/lida`, {});
    await unwrapData<IdCriado>(response, 'Não foi possível marcar a mensagem como lida.');
  },

  contarNaoLidasChat: async (processoId: string): Promise<number> => {
    const response = await portalGet(`${autosBase(processoId)}/chat/nao-lidas`);
    const data = await unwrapData<{ total?: number }>(
      response,
      'Não foi possível contar as não lidas.',
    );
    return Number(data.total ?? 0);
  },

  listarSolicitacoes: async (processoId: string): Promise<SolicitacaoAlteracao[]> => {
    const response = await portalGet(`${autosBase(processoId)}/solicitacoes`);
    return unwrapData<SolicitacaoAlteracao[]>(
      response,
      'Não foi possível carregar as solicitações.',
    );
  },

  criarSolicitacao: async (
    processoId: string,
    { anexos, ...corpo }: CriarSolicitacaoPayload,
  ): Promise<SolicitacaoAlteracao> => {
    const body = anexos?.length ? montarMultipartSolicitacao(corpo, anexos) : corpo;
    const response = await portalPost(`${autosBase(processoId)}/solicitacoes`, body);
    return unwrapData<SolicitacaoAlteracao>(response, 'Não foi possível enviar a solicitação.');
  },
};
